const DEFAULT_MAX_LOGS = 200;

const normalizeBoolean = (value) => {
  if (typeof value !== 'string') return false;
  switch (value.trim().toLowerCase()) {
    case '1':
    case 'true':
    case 'yes':
    case 'on':
      return true;
    default:
      return false;
  }
};

const resolveFlag = (flagName) => {
  const params = new URLSearchParams(window.location.search);
  if (params.has(flagName)) {
    const isEnabled = normalizeBoolean(params.get(flagName));
    try {
      localStorage.setItem(`debug:${flagName}`, String(isEnabled));
    } catch (e) {
      // ignore storage issues
    }
    return isEnabled;
  }

  try {
    return normalizeBoolean(localStorage.getItem(`debug:${flagName}`));
  } catch (e) {
    return false;
  }
};

const createOverlayElements = () => {
  const panel = document.createElement('div');
  panel.id = 'debug-overlay-panel';
  panel.style.cssText = [
    'position:fixed',
    'bottom:56px',
    'left:12px',
    'right:12px',
    'max-height:50vh',
    'overflow-y:auto',
    'z-index:2147483647',
    'font-family:Menlo,Monaco,Consolas,monospace',
    'font-size:12px',
    'line-height:1.4',
    'color:#0ff',
    'background:rgba(0,0,0,0.9)',
    'padding:12px 14px',
    'border-radius:10px',
    'box-shadow:0 12px 32px rgba(0,0,0,0.55)',
    'display:none',
    'gap:6px',
  ].join(';');

  const toggle = document.createElement('button');
  toggle.id = 'debug-overlay-toggle';
  toggle.type = 'button';
  toggle.textContent = 'Показать логи';
  toggle.style.cssText = [
    'position:fixed',
    'bottom:12px',
    'right:12px',
    'z-index:2147483647',
    'padding:7px 16px',
    'font-family:Menlo,Monaco,Consolas,monospace',
    'font-size:12px',
    'color:#0ff',
    'background:rgba(0,0,0,0.78)',
    'border:1px solid rgba(0,255,255,0.45)',
    'border-radius:999px',
    'cursor:pointer',
    'opacity:0.82',
    'transition:opacity 0.2s ease,background 0.2s ease',
  ].join(';');

  toggle.addEventListener('mouseenter', () => {
    toggle.style.opacity = '1';
    toggle.style.background = 'rgba(0,0,0,0.92)';
  });

  toggle.addEventListener('mouseleave', () => {
    toggle.style.opacity = '0.82';
    toggle.style.background = 'rgba(0,0,0,0.78)';
  });

  document.body.appendChild(panel);
  document.body.appendChild(toggle);

  return { panel, toggle };
};

const createOverlay = ({ maxLogs } = {}) => {
  const { panel, toggle } = createOverlayElements();
  const buffer = [];
  const limit = typeof maxLogs === 'number' ? maxLogs : DEFAULT_MAX_LOGS;
  let expanded = false;

  const updateToggleLabel = () => {
    toggle.textContent = expanded ? 'Скрыть логи' : 'Показать логи';
  };

  const showPanel = () => {
    if (!expanded) {
      expanded = true;
      panel.style.display = 'block';
      updateToggleLabel();
      requestAnimationFrame(() => {
        panel.scrollTop = panel.scrollHeight;
      });
    }
  };

  const hidePanel = () => {
    if (expanded) {
      expanded = false;
      panel.style.display = 'none';
      updateToggleLabel();
    }
  };

  const togglePanel = () => {
    if (expanded) {
      hidePanel();
    } else {
      showPanel();
    }
  };

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    togglePanel();
  });

  const addEntry = (type, payload) => {
    const row = document.createElement('pre');
    row.style.margin = '0 0 8px 0';
    row.style.whiteSpace = 'pre-wrap';
    row.style.wordBreak = 'break-word';
    row.textContent = `${new Date().toLocaleTimeString()} [${type}] ${payload}`;

    buffer.push(row);
    panel.appendChild(row);

    if (buffer.length > limit) {
      const removed = buffer.splice(0, buffer.length - limit);
      removed.forEach((node) => {
        if (node && node.parentNode === panel) {
          panel.removeChild(node);
        }
      });
    }

    if (expanded) {
      panel.scrollTop = panel.scrollHeight;
    }
  };

  updateToggleLabel();

  return {
    addEntry,
    showPanel,
  };
};

const stringifyPayload = (value) => {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const hookConsole = (overlay, consoleObject) => {
  const passthrough = consoleObject || window.console;
  const methods = ['log', 'info', 'warn', 'error', 'debug'];

  methods.forEach((method) => {
    if (typeof passthrough[method] !== 'function') return;

    const original = passthrough[method].bind(passthrough);
    passthrough[method] = (...args) => {
      const serialized = args.map(stringifyPayload).join(' ');
      overlay.addEntry(method, serialized);
      original(...args);
    };
  });
};

const hookBridge = (overlay, bridge) => {
  if (!bridge || typeof bridge.subscribe !== 'function') return;

  bridge.subscribe((event) => {
    const payload = event && event.detail ? event.detail : event;
    overlay.addEntry('vkBridge', stringifyPayload(payload));
  });
};

const initDebugOverlay = (options = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  if (window.__debugOverlayEnabled) {
    return;
  }

  const { flag = 'development', bridge = null, console: consoleObject = null, maxLogs } = options;

  const isEnabled = resolveFlag(flag);

  if (!isEnabled) {
    return;
  }

  window.__debugOverlayEnabled = true;

  const overlay = createOverlay({ maxLogs });
  overlay.addEntry('debug', `Overlay активирован параметром "${flag}".`);

  hookConsole(overlay, consoleObject);
  hookBridge(overlay, bridge);
};

export default initDebugOverlay;

